import { useCallback, useEffect, useRef, useState } from 'react';
import { overcallingAssignDifficultyApi } from '../api/overcallingAssignDifficultyApi';
import type {
  AssignDifficultyCandidateItem,
  AssignDifficultyFilter,
  DifficultyRecord,
} from '../types/overcallingAssignDifficulty';

interface HookState {
  filter: AssignDifficultyFilter;
  items: AssignDifficultyCandidateItem[];
  currentIndex: number;
  counterTotal: number;
  counterPosition: number;
  loading: boolean;
  /** True while an Assign is in flight (drives the in-flight disable rule per Edge Cases). */
  assigning: boolean;
  // Mirrors the 009 Data Editing auto-advance (Session 2026-05-29): true during the 1-second
  // post-Assign confirmation window, before the timed auto-advance fires. While either
  // `loading`, `assigning`, or `autoAdvancing` is true the page disables every interactive
  // control and covers the gray content container with the auto-advance mask overlay.
  autoAdvancing: boolean;
  error: string | null;
  /** Inline confirmation text after a successful Assign; cleared on navigation / filter / refresh. */
  confirmationMessage: string | null;
  /** Inline error text after a failed Assign; cleared on next Assign attempt. */
  assignError: string | null;
  /** Per-tableresultId Assign-count counter — drives the React-key flash cancellation pattern. */
  assignSeq: Record<number, number>;
  /** Flips true on a successful Assign so DifficultyIndicator triggers its highlight. */
  flashRequested: boolean;
}

const INITIAL_STATE: HookState = {
  filter: 'NotAssigned',
  items: [],
  currentIndex: 0,
  counterTotal: 0,
  counterPosition: 0,
  loading: false,
  assigning: false,
  autoAdvancing: false,
  error: null,
  confirmationMessage: null,
  assignError: null,
  assignSeq: {},
  flashRequested: false,
};

const CONFIRMATION_TEXT = "'Level of difficulty' assigned.";

// After a successful Assign the page holds the confirmation mask for this many milliseconds —
// controls disabled, gray container masked — then auto-advances to the next hand exactly as a
// Next click would. Short, non-destructive, and reversible via Previous on the next load, so
// the timed advance is not user-cancellable. Shortened to 500ms after client sign-off
// (2026-05-29).
const AUTO_ADVANCE_DELAY_MS = 500;

// Pure index advance shared by manual Next and the timed auto-advance: move to the next hand
// and clear the transient Assign feedback. Non-wrapping (Session 2026-05-29): on the last hand
// there is no next, so it stays put — Next is disabled there and the auto-advance is not
// scheduled in that case.
function advanceState(prev: HookState): HookState {
  const cleared = { confirmationMessage: null, assignError: null, flashRequested: false };
  if (prev.items.length === 0) {
    return { ...prev, ...cleared };
  }
  const nextIndex = prev.currentIndex + 1;
  if (nextIndex >= prev.items.length) {
    return { ...prev, ...cleared };
  }
  return { ...prev, ...cleared, currentIndex: nextIndex, counterPosition: nextIndex + 1 };
}

export function useOvercallingAssignDifficultySession() {
  const [state, setState] = useState<HookState>(INITIAL_STATE);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id stamped on each loadCandidates call. Only the response of the
  // most-recently-started load is applied; earlier in-flight loads are ignored
  // when they resolve. This guards against (a) React 18 StrictMode double-invoking
  // the mount effect — which fires two concurrent NotAssigned loads, the later of
  // which would otherwise resolve after an Assign click and wipe the in-memory
  // working set back to a fresh, unrated pool (resetting the indicator to N/A and
  // the counter to Hand 1) — and (b) the real-user race of rapidly switching the
  // Data-category filter or double-clicking Refresh, where a slower earlier
  // response must not overwrite the latest selection. Mirrors the identical guard
  // in useOvercallingDataEditingSession (the 009 hook this one was cloned from).
  const loadIdRef = useRef(0);

  const clearAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer.current !== null) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
  }, []);

  // Hold the confirmation for AUTO_ADVANCE_DELAY_MS, then auto-advance and re-enable the
  // controls (autoAdvancing -> false). The setState reads the latest state at fire time, so it
  // advances from wherever the Assign left the index.
  const scheduleAutoAdvance = useCallback(() => {
    clearAutoAdvance();
    autoAdvanceTimer.current = setTimeout(() => {
      autoAdvanceTimer.current = null;
      setState(prev => ({ ...advanceState(prev), autoAdvancing: false }));
    }, AUTO_ADVANCE_DELAY_MS);
  }, [clearAutoAdvance]);

  // Cancel any pending auto-advance when the component unmounts.
  useEffect(() => () => clearAutoAdvance(), [clearAutoAdvance]);

  const loadCandidates = useCallback(async (filter: AssignDifficultyFilter) => {
    clearAutoAdvance();
    const myLoadId = ++loadIdRef.current;
    setState(prev => ({
      ...prev,
      loading: true,
      autoAdvancing: false,
      error: null,
      confirmationMessage: null,
      assignError: null,
      flashRequested: false,
    }));
    try {
      const response = await overcallingAssignDifficultyApi.getCandidates(filter);
      // Drop the response if a newer load has since started (StrictMode double-mount,
      // or a rapid filter/Refresh change) — applying it would clobber the latest state,
      // including an in-memory Assign that landed while this fetch was in flight.
      if (loadIdRef.current !== myLoadId) return;
      const items = response.items;
      setState({
        filter,
        items,
        currentIndex: 0,
        counterTotal: items.length,
        counterPosition: items.length > 0 ? 1 : 0,
        loading: false,
        assigning: false,
        autoAdvancing: false,
        error: null,
        confirmationMessage: null,
        assignError: null,
        assignSeq: {},
        flashRequested: false,
      });
    } catch (err) {
      if (loadIdRef.current !== myLoadId) return;
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load candidates',
      }));
    }
  }, [clearAutoAdvance]);

  // Initial load of the default filter.
  useEffect(() => {
    loadCandidates('NotAssigned');
  }, [loadCandidates]);

  // Assign upserts the current hand's difficulty and leaves the working-set list,
  // currentIndex, and counterTotal untouched (the hand stays in the snapshot so Previous can
  // return to it). When a next hand exists the confirmation mask shows, every control stays
  // disabled (autoAdvancing), and after AUTO_ADVANCE_DELAY_MS the page auto-advances to it.
  // On the LAST hand there is nowhere to advance (non-wrapping), so it saves, shows the inline
  // confirmation, and stays put — no mask, no timer. On a failed save no auto-advance is
  // scheduled and the controls re-enable for retry.
  const applyAssign = useCallback(async (level: number) => {
    clearAutoAdvance();
    const current = state.items[state.currentIndex];
    if (!current) return;
    const hasNext = state.currentIndex < state.items.length - 1;
    setState(prev => ({ ...prev, assigning: true, assignError: null, confirmationMessage: null, flashRequested: false }));
    try {
      const record: DifficultyRecord = await overcallingAssignDifficultyApi.assignDifficulty(
        current.tableResultId,
        level
      );
      setState(prev => {
        const newItems = prev.items.slice();
        newItems[prev.currentIndex] = {
          ...current,
          difficultyLevel: record.difficultyLevel,
          lastUpdatedBy: record.lastUpdatedBy,
          lastUpdatedAt: record.lastUpdatedAt,
        };
        const prevSeq = prev.assignSeq[current.tableResultId] ?? 0;
        return {
          ...prev,
          items: newItems,
          assigning: false,
          autoAdvancing: hasNext,
          confirmationMessage: CONFIRMATION_TEXT,
          assignError: null,
          assignSeq: { ...prev.assignSeq, [current.tableResultId]: prevSeq + 1 },
          flashRequested: true,
        };
      });
      if (hasNext) scheduleAutoAdvance();
    } catch (err) {
      setState(prev => ({
        ...prev,
        assigning: false,
        assignError: err instanceof Error ? err.message : 'Failed to assign difficulty',
        flashRequested: false,
      }));
    }
  }, [state.items, state.currentIndex, clearAutoAdvance, scheduleAutoAdvance]);

  const advanceNext = useCallback(() => {
    clearAutoAdvance();
    setState(prev => ({ ...advanceState(prev), autoAdvancing: false }));
  }, [clearAutoAdvance]);

  const advancePrev = useCallback(() => {
    clearAutoAdvance();
    setState(prev => {
      const cleared = { confirmationMessage: null, assignError: null, flashRequested: false, autoAdvancing: false };
      // Non-wrapping (Session 2026-05-29): on the first hand there is no previous, so stay put
      // — Previous is disabled there.
      if (prev.items.length === 0 || prev.currentIndex <= 0) {
        return { ...prev, ...cleared };
      }
      const prevIndex = prev.currentIndex - 1;
      return { ...prev, ...cleared, currentIndex: prevIndex, counterPosition: prevIndex + 1 };
    });
  }, [clearAutoAdvance]);

  const currentItem = state.items[state.currentIndex] ?? null;
  const currentAssignSeq = currentItem ? (state.assignSeq[currentItem.tableResultId] ?? 0) : 0;
  const flashKey = currentItem ? `${currentItem.tableResultId}-${currentAssignSeq}` : 'empty';

  return {
    // state
    filter: state.filter,
    items: state.items,
    currentItem,
    currentIndex: state.currentIndex,
    counterTotal: state.counterTotal,
    counterPosition: state.counterPosition,
    loading: state.loading,
    assigning: state.assigning,
    autoAdvancing: state.autoAdvancing,
    error: state.error,
    confirmationMessage: state.confirmationMessage,
    assignError: state.assignError,
    flashKey,
    flashRequested: state.flashRequested,

    // actions
    loadCandidates,
    applyAssign,
    advanceNext,
    advancePrev,
  };
}
